/**
 * ═══════════════════════════════════════════════════════════════════════════════
 * PLASMA WAVE — Audio-reactive WebGL shader visualization
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * This is the bridge from Canvas 2D to GPU-powered shader visualizations.
 * A GLSL fragment shader creates a flowing plasma/wave pattern driven by
 * bass, mid, and treble energy + time.
 *
 * HOW WebGL SHADERS WORK (the basics):
 * 1. We create a full-screen quad (two triangles covering the canvas).
 * 2. The GPU runs our "fragment shader" for EVERY pixel, in parallel.
 * 3. The shader receives "uniforms" — values we send from JavaScript each frame:
 *    - u_time: elapsed seconds (for animation)
 *    - u_bass, u_mid, u_treble: audio energy (0-1)
 *    - u_beat: beat intensity (0-1, decays)
 *    - u_resolution: canvas size in pixels
 * 4. The shader calculates a color for each pixel based on its position and the uniforms.
 * 5. The math uses sin/cos waves layered together to create the plasma pattern.
 *
 * TO LEARN MORE: Visit shadertoy.com — thousands of shader examples to study.
 * The pattern is: pass data as uniforms, write math in the fragment shader.
 */

import { Visualization, FrameData, VisualizationSetting } from '../../types';
import { BeatPulse } from '../../utils/audioUtils';

// ── GLSL Shader Source Code ─────────────────────────────────────────────────
// This is the "vertex shader" — it just positions the full-screen quad.
// Every WebGL program needs one, but for 2D effects it's always the same boilerplate.
const VERTEX_SHADER = `
  attribute vec2 a_position;
  void main() {
    gl_Position = vec4(a_position, 0.0, 1.0);
  }
`;

// This is the "fragment shader" — the creative part!
// It runs for every pixel and decides its color.
const FRAGMENT_SHADER = `
  precision mediump float;

  // Uniforms sent from JavaScript each frame
  uniform float u_time;       // elapsed seconds
  uniform float u_bass;       // bass energy 0-1
  uniform float u_mid;        // mid energy 0-1
  uniform float u_treble;     // treble energy 0-1
  uniform float u_beat;       // beat intensity (decays)
  uniform vec2 u_resolution;  // canvas size in pixels
  uniform float u_speed;      // animation speed multiplier

  void main() {
    // Normalize pixel coordinates to 0-1 range (bottom-left origin)
    vec2 uv = gl_FragCoord.xy / u_resolution;

    // Center the coordinates (-0.5 to 0.5) for radial effects
    vec2 centered = uv - 0.5;

    // Distance from center (for radial patterns)
    float dist = length(centered);

    // Time, modulated by speed
    float t = u_time * u_speed;

    // ── PLASMA CALCULATION ────────────────────────────────────────────
    // Layer multiple sine waves at different frequencies and phases.
    // Audio energy modulates the frequency and amplitude of each layer.

    // Layer 1: horizontal waves, frequency driven by bass
    float wave1 = sin(uv.x * (6.0 + u_bass * 8.0) + t * 1.5);

    // Layer 2: vertical waves, frequency driven by treble
    float wave2 = sin(uv.y * (5.0 + u_treble * 10.0) + t * 1.2 + 1.57);

    // Layer 3: radial pulse from center, frequency driven by mid
    float wave3 = sin(dist * (8.0 + u_mid * 12.0) - t * 2.0);

    // Layer 4: diagonal wave for complexity
    float wave4 = sin((uv.x + uv.y) * 4.0 + t * 0.8);

    // Combine layers (average, then remap from -1..1 to 0..1)
    float plasma = (wave1 + wave2 + wave3 + wave4) / 4.0;
    plasma = plasma * 0.5 + 0.5;

    // ── COLORING ──────────────────────────────────────────────────────
    // Map the plasma value to color using sine waves offset by 120° (RGB phase shift)
    // This creates smooth rainbow-like color cycling.
    float r = sin(plasma * 6.28 + 0.0 + u_bass * 3.0) * 0.5 + 0.5;
    float g = sin(plasma * 6.28 + 2.09 + u_mid * 2.0) * 0.5 + 0.5;
    float b = sin(plasma * 6.28 + 4.18 + u_treble * 2.5) * 0.5 + 0.5;

    // Beat flash: brighten everything on beat
    float beatFlash = u_beat * 0.3;
    r = min(1.0, r + beatFlash);
    g = min(1.0, g + beatFlash);
    b = min(1.0, b + beatFlash);

    // Slight vignette (darker edges)
    float vignette = 1.0 - dist * 0.8;
    r *= vignette;
    g *= vignette;
    b *= vignette;

    gl_FragColor = vec4(r, g, b, 1.0);
  }
`;

export class PlasmaWave implements Visualization {
  id = 'plasma-wave';
  name = 'Plasma Wave';
  category = 'shader' as const;
  description = 'Audio-reactive plasma pattern rendered with WebGL shaders';
  renderer = 'webgl' as const;

  private canvas!: HTMLCanvasElement;
  private gl!: WebGLRenderingContext;
  private program!: WebGLProgram;
  private beatPulse = new BeatPulse(0.93);

  // Uniform locations (cached for performance)
  private uTime!: WebGLUniformLocation | null;
  private uBass!: WebGLUniformLocation | null;
  private uMid!: WebGLUniformLocation | null;
  private uTreble!: WebGLUniformLocation | null;
  private uBeat!: WebGLUniformLocation | null;
  private uResolution!: WebGLUniformLocation | null;
  private uSpeed!: WebGLUniformLocation | null;

  private speed = 0.8;

  init(canvas: HTMLCanvasElement): void {
    this.canvas = canvas;
    const gl = canvas.getContext('webgl');
    if (!gl) throw new Error('WebGL not supported');
    this.gl = gl;

    // Compile shaders and link program
    this.program = this.createProgram(VERTEX_SHADER, FRAGMENT_SHADER);
    gl.useProgram(this.program);

    // Create a full-screen quad (two triangles covering -1 to 1)
    const vertices = new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]);
    const buffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(gl.ARRAY_BUFFER, vertices, gl.STATIC_DRAW);

    const aPosition = gl.getAttribLocation(this.program, 'a_position');
    gl.enableVertexAttribArray(aPosition);
    gl.vertexAttribPointer(aPosition, 2, gl.FLOAT, false, 0, 0);

    // Cache uniform locations
    this.uTime = gl.getUniformLocation(this.program, 'u_time');
    this.uBass = gl.getUniformLocation(this.program, 'u_bass');
    this.uMid = gl.getUniformLocation(this.program, 'u_mid');
    this.uTreble = gl.getUniformLocation(this.program, 'u_treble');
    this.uBeat = gl.getUniformLocation(this.program, 'u_beat');
    this.uResolution = gl.getUniformLocation(this.program, 'u_resolution');
    this.uSpeed = gl.getUniformLocation(this.program, 'u_speed');
  }

  render(frame: FrameData): void {
    const { bassEnergy, midEnergy, trebleEnergy, isBeat, beatIntensity, elapsed } = frame;
    const gl = this.gl;

    this.beatPulse.update(isBeat, beatIntensity);

    // Send audio data to the shader as uniforms
    gl.uniform1f(this.uTime, elapsed / 1000);
    gl.uniform1f(this.uBass, bassEnergy);
    gl.uniform1f(this.uMid, midEnergy);
    gl.uniform1f(this.uTreble, trebleEnergy);
    gl.uniform1f(this.uBeat, this.beatPulse.value);
    gl.uniform2f(this.uResolution, this.canvas.width, this.canvas.height);
    gl.uniform1f(this.uSpeed, this.speed);

    // Draw the full-screen quad (6 vertices = 2 triangles)
    gl.drawArrays(gl.TRIANGLES, 0, 6);
  }

  resize(width: number, height: number): void {
    // Don't re-set canvas.width/height — the caller already did that and
    // setting them again would clear the canvas + reset WebGL state a second time.
    this.gl.viewport(0, 0, width, height);
  }

  destroy(): void {
    if (this.gl && this.program) {
      this.gl.deleteProgram(this.program);
    }
  }

  getSettings(): VisualizationSetting[] {
    return [
      { key: 'speed', label: 'Speed', type: 'range', value: this.speed, min: 0.1, max: 3, step: 0.1 },
    ];
  }

  setSettings(settings: Record<string, number | string | boolean>): void {
    if (typeof settings.speed === 'number') this.speed = settings.speed;
  }

  // ── WebGL helpers ──────────────────────────────────────────────────────────

  private createProgram(vertexSrc: string, fragmentSrc: string): WebGLProgram {
    const gl = this.gl;
    const vs = this.compileShader(gl.VERTEX_SHADER, vertexSrc);
    const fs = this.compileShader(gl.FRAGMENT_SHADER, fragmentSrc);
    const program = gl.createProgram()!;
    gl.attachShader(program, vs);
    gl.attachShader(program, fs);
    gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      throw new Error('Shader link error: ' + gl.getProgramInfoLog(program));
    }
    return program;
  }

  private compileShader(type: number, source: string): WebGLShader {
    const gl = this.gl;
    const shader = gl.createShader(type)!;
    gl.shaderSource(shader, source);
    gl.compileShader(shader);
    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
      throw new Error('Shader compile error: ' + gl.getShaderInfoLog(shader));
    }
    return shader;
  }
}
